import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight } from 'lucide-react';
import MainNavbar from '@/components/layout/MainNavbar';
import SEO from '@/components/SEO';
import categories from '@/data/categories.json';
import { tierStyle } from '@/lib/tierStyles';
import {
  ExpertBlock,
  PricingBlock,
  ReviewsFAQBlock,
  SitemapBlock,
  SiteFooter,
} from '@/components/landing/HomeSections';


/**
 * Universal category page (Любовь / Деньги / Реализация / Прогноз).
 * Все данные — из /app/frontend/src/data/categories.json.
 * Никаких хардкодов калькуляторов/текстов в коде.
 */
export default function CategoryPage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const cat = categories[slug];
  const navigate = useNavigate();

  if (!cat) {
    return (
      <div className="min-h-screen bg-white">
        <MainNavbar />
        <div className="pt-24 text-center text-slate-500">Категория не найдена</div>
      </div>
    );
  }

  // Вход без стены (29.09.2026): карточка ведёт прямо в программу — и гостя,
  // и вошедшего без тарифа. Регистрацию и ПРО программа спросит сама, на
  // «Рассчитать» или на размытых блоках результата.
  const handleCalcClick = (c) => {
    navigate(c.path);
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`category-page-${slug}`}>
      <SEO page={`category-${slug}`} canonicalPath={cat.route} />
      <MainNavbar />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="pt-[64px] md:pt-[72px] pb-8 md:pb-12 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="order-2 md:order-1">
            <h1 className="text-3xl md:text-5xl font-heading font-black text-slate-900 mb-2" data-testid="category-h1">
              {cat.hero.heading}
            </h1>
            <p className="text-base md:text-lg text-accent-cta-600 font-medium mb-4">{cat.hero.subheading}</p>
            <p className="text-sm md:text-base text-slate-600 leading-relaxed max-w-lg">{cat.hero.description}</p>
          </div>
          <div className="order-1 md:order-2 flex justify-center">
            <img
              src={cat.hero.image}
              alt={cat.title}
              className="w-full max-w-[380px] md:max-w-[440px] rounded-2xl object-contain"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* ═══════════════ CALCULATORS ═══════════════ */}
      <section className="bg-accent-cta-50/50 py-10 md:py-14 px-4" data-testid="category-calculators">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-center text-slate-900 mb-8">
            Выбери свои программы
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {cat.calculators.map((c) => {
              // Меток тарифа на карточках нет (29.09.2026): все карточки
              // одного вида, доступ решается внутри программы.
              const t = tierStyle('FREE');
              const slugSafe = c.path.replace('/', '');
              return (
                <div
                  key={c.path}
                  data-testid={`calc-card-${slugSafe}`}
                  className={`relative flex flex-col ${t.cardBg} rounded-2xl border-2 ${t.cardBorder} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5 md:p-6`}
                >
                  <h3 className="text-lg md:text-xl font-heading font-bold text-slate-900 mb-2">{c.name}</h3>
                  <p className="text-sm text-slate-600 mb-3 flex-1 leading-relaxed">{c.description}</p>
                  <Button
                    className={`w-full ${t.btnClass} font-bold`}
                    onClick={() => handleCalcClick(c)}
                    data-testid={`calc-cta-${slugSafe}`}
                  >
                    ПЕРЕЙТИ
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════ BENEFITS ═══════════════ */}
      <section className="bg-white py-10 md:py-14 px-4" data-testid="category-benefits">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-center text-slate-900 mb-8">
            Что ты получишь
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {cat.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3 bg-accent-cta-50 rounded-xl px-4 py-3">
                <Check className="w-5 h-5 text-accent-cta-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base text-slate-800">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══════════════ EXPERT ═══════════════ */}
      <ExpertBlock />

      {/* ═══════════════ REVIEWS + FAQ (категорийный FAQ) ═══════════════ */}
      <ReviewsFAQBlock faq={cat.faq} />

      {/* ═══════════════ PRICING ═══════════════ */}
      <PricingBlock />

      {/* ═══════════════ SITEMAP + FOOTER ═══════════════ */}
      <SitemapBlock />
      <SiteFooter />
    </div>
  );
}
